import { FC } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import { PropsWithClassName } from '@/types/PropsWithClassName';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { CallLog } from '@/types/models/CallLog';
import { formatDuration } from '@/utils/formatDuration.ts';
import Toggle from '@/components/shared/Toggle/Toggle.tsx';
import DisplayReservationCommunicationLogModal from '@/components/modals/ReservationLogs/DisplayReservationCommunicationLogModal.tsx';

type TableRow = CallLog;

type ReservationLogsTableProps = {
  rows: TableRow[];
};

const ReservationLogsTable: FC<PropsWithClassName<ReservationLogsTableProps>> = ({ rows, className }) => {
  const { t } = useTranslation();

  const columns: GridColDef[] = [
    {
      field: 'channel',
      headerName: t('tableLabel.channel'),
      width: 250,
      renderCell: (params) => params.row?.channel ?? '-',
    },
    {
      field: 'duration',
      headerName: t('tableLabel.duration'),
      width: 250,
      renderCell: (params) => (params.row.duration ? formatDuration(params.row.duration) : '-'),
    },
    {
      field: 'date',
      headerName: t('tableLabel.date'),
      width: 250,
      cellClassName: 'flex items-center',
      renderCell: (params) => dayjs(params.row.date).format('MM/DD/YY'),
    },
    {
      field: 'time',
      headerName: t('tableLabel.time'),
      width: 250,
      cellClassName: 'flex items-center',
      renderCell: (params) => dayjs(params.row.date).format('h:mm a'),
    },
    {
      field: 'conversation_log',
      headerName: t('tableLabel.conversationLog'),
      width: 250,
      renderCell: (params) => (
        <Toggle
          renderTrigger={({ toggle }) => (
            <button className="text-primary-400 underline border-none bg-transparent cursor-pointer" onClick={toggle}>
              {t('components.reservationLogsTable.viewLogs')}
            </button>
          )}
          renderContent={({ isOpen, handleClose }) => (
            <DisplayReservationCommunicationLogModal isOpen={isOpen} onClose={handleClose} callLogId={params.row.id} />
          )}
        />
      ),
      valueGetter: (_value, row) => row.id,
    },
  ];

  const paginationModel = { page: 0, pageSize: 10 };

  return (
    <Paper sx={{ width: '100%' }} className={className}>
      <DataGrid
        getRowId={(row) => row.id}
        rows={rows}
        columns={columns}
        initialState={{ pagination: { paginationModel } }}
        pageSizeOptions={[10, 20, 50, 100]}
        rowSelection={false}
      />
    </Paper>
  );
};

export default ReservationLogsTable;
